import { useCallback, useState } from 'react';
import type { ContentItem } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import type { ClipboardPayload } from './clipboardPayload';
import { decideCanvasPaste } from './canvasSmartPasteUtils';
import { classifyFile } from './tableImport';
import { useClipboardPaste } from './useClipboardPaste';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

export interface CanvasSmartPasteConfig {
  modelId: string;
  modelType: string;
  /** Omitted disables file/image extraction — table/text pastes still resolve client-side. */
  backendService?: BackendService;
  onResolved: (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => void;
}

/**
 * The empty-canvas drop zone's "click to paste" / file-drop affordance — resolves whatever lands
 * on it (a clipboard paste, or a dropped Excel/Word/PDF/image file) into the best-fit block(s)
 * with no existing block to anchor the decision, unlike the per-block Paste button. Structural
 * or prose text is classified client-side (`decideCanvasPaste`); anything else (an image, or any
 * dropped file) goes through the same unscoped AI extraction the Documents tab uses.
 */
export function useCanvasSmartPaste(config: CanvasSmartPasteConfig) {
  const { modelId, modelType, backendService, onResolved } = config;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractFromFile = useCallback(
    async (file: File) => {
      if (!backendService) {
        setError('Could not read that file.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const { blocks } = await backendService.extractBlockSectionsFromFile(
          modelType,
          modelId,
          file,
        );
        if (blocks.length === 0) {
          setError('Could not find anything usable in that file.');
          return;
        }
        onResolved(blocks as ContentItem[]);
      } catch (err) {
        setError((err as Error)?.message ?? 'Could not read that file.');
      } finally {
        setBusy(false);
      }
    },
    [backendService, modelId, modelType, onResolved],
  );

  const consume = useCallback(
    (payload: ClipboardPayload) => {
      setError(null);
      const result = decideCanvasPaste(payload, modelId, modelType);
      if (result.kind === 'empty') {
        setError(result.reason);
        return;
      }
      if (result.kind === 'image') {
        void extractFromFile(result.file);
        return;
      }
      onResolved(result.blocks);
    },
    [extractFromFile, modelId, modelType, onResolved],
  );

  const clip = useClipboardPaste(consume);

  const dropFile = useCallback(
    async (file: File) => {
      setError(null);
      if (classifyFile(file) === 'delimited') {
        consume({ html: '', text: await file.text().catch(() => ''), image: null });
        return;
      }
      void extractFromFile(file);
    },
    [consume, extractFromFile],
  );

  return {
    run: clip.run,
    busy: busy || clip.busy,
    armed: clip.armed,
    error: error ?? clip.error,
    hiddenInputProps: clip.hiddenInputProps,
    dropFile,
  };
}
